import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

const sanitizeNotificationParams = (params = {}) => {
  const nextParams = { ...params };
  delete nextParams.force;
  return nextParams;
};

// FETCH ALL NOTIFICATIONS
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { lastFetchedAt, isLoading } = getState().notifications;
      const staleTime = 30000;

      if (
        !params.force &&
        lastFetchedAt &&
        Date.now() - lastFetchedAt < staleTime &&
        !isLoading
      ) {
        return rejectWithValue("stale_skip");
      }

      const requestParams = sanitizeNotificationParams(params);
      const res = await Axios.get("/tenant/notifications", {
        params: requestParams,
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return {
        ...res.data.data,
        isSearch: !!requestParams?.search,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch notifications",
      );
    }
  },
  {
    condition: (params, { getState }) => {
      const { notifications } = getState();
      if (notifications?.isLoading) {
        return false;
      }
    },
  },
);

// FETCH DROPDOWN NOTIFICATIONS (Header Bell)
export const fetchDropdownNotifications = createAsyncThunk(
  "notifications/fetchDropdown",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/notifications", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch notifications",
      );
    }
  },
);

// MARK A SINGLE NOTIFICATION AS READ
export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/tenant/notifications/${id}/mark-read`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return id;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to mark notification as read",
      );
    }
  },
);

// MARK ALL NOTIFICATIONS AS READ
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/notifications/mark-all-read");
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          "Failed to mark all notifications as read",
      );
    }
  },
);

// DELETE A SINGLE NOTIFICATION
export const deleteNotification = createAsyncThunk(
  "notifications/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/tenant/notifications/${id}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return id;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete notification",
      );
    }
  },
);

// BULK DELETE NOTIFICATIONS
export const bulkDeleteNotifications = createAsyncThunk(
  "notifications/bulkDelete",
  async (ids, { rejectWithValue }) => {
    try {
      const res = await Axios.delete("/tenant/notifications/bulk-delete", {
        data: { ids },
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return ids;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete notifications",
      );
    }
  },
);

// FETCH NOTIFICATION SETTINGS
export const fetchNotificationSettings = createAsyncThunk(
  "notifications/fetchSettings",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/notification-settings", {
        params: userId ? { userId } : {},
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch notification settings",
      );
    }
  },
);

// UPDATE NOTIFICATION SETTING
export const updateNotificationSetting = createAsyncThunk(
  "notifications/updateSetting",
  async (
    { module_name, email_enabled, bell_enabled, userId },
    { rejectWithValue },
  ) => {
    try {
      const res = await Axios.post(
        "/tenant/notification-settings/update",
        {
          module_name,
          email_enabled,
          bell_enabled,
        },
        {
          params: userId ? { userId } : {},
        },
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update notification setting",
      );
    }
  },
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState: {
    items: [],
    totalUnread: 0,
    totalRecords: 0,
    lastFetchedAt: null,
    lastRequestParams: null,
    isLoading: false,

    isMarkingRead: false,
    error: null,
    settings: [],
    settingsLoading: false,
    settingsSaving: false,
    settingsError: null,
  },

  reducers: {
    clearNotificationError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      //  FETCH ALL NOTIFICATIONS
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastFetchedAt = Date.now();
        state.totalUnread = action.payload?.total_unread ?? 0;
        state.totalRecords = action.payload?.total_records ?? 0;
        state.lastRequestParams = action.payload?.requestParams ?? {};
        state.items = action.payload?.data ?? [];
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload !== "stale_skip") {
          state.error = action.payload;
        }
      })

      // FETCH DROPDOWN NOTIFICATIONS
      .addCase(fetchDropdownNotifications.pending, (state) => {
        state.dropdownLoading = true;
      })
      .addCase(fetchDropdownNotifications.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownItems = action.payload?.data ?? [];
        state.dropdownTotalUnread = action.payload?.total_unread ?? 0;
      })
      .addCase(fetchDropdownNotifications.rejected, (state) => {
        state.dropdownLoading = false;
      })

      // FETCH SETTINGS
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload ?? [];
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload;
      });
  },
});

export const { clearNotificationError } = notificationSlice.actions;
export default notificationSlice.reducer;
